"use client";

import { useState } from "react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import ProjectModal from "@/components/ProjectModal";

interface ProjectCardProps {
  project: {
    id: number;
    title: string;
    description: string;
    sponsored: boolean;
    sponsor?: string;
    status: string;
  };
}

export default function ProjectCard({ project }: ProjectCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <>
      <Card 
        className="hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group"
        onClick={openModal}
      >
        <CardHeader>
          {/* Project Status Badge */}
          <div className="flex items-center justify-between mb-4">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              project.status === 'ongoing' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-blue-100 text-blue-800'
            }`}>
              {project.status === 'ongoing' ? 'Ongoing' : 'Completed'}
            </span>
            
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              project.sponsored 
                ? 'bg-orange-100 text-orange-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {project.sponsored ? 'Sponsored' : 'Collaboration'}
            </span>
          </div>

          <CardTitle className="text-xl group-hover:text-primary transition-colors duration-200">
            {project.title}
          </CardTitle>
        </CardHeader>

        <CardContent>
          {/* Sponsor Info */}
          {project.sponsored && project.sponsor && (
            <div className="flex items-start text-gray-600 mb-4">
              <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span className="text-sm">{project.sponsor}</span>
            </div>
          )}
        </CardContent>

        <CardFooter>
          {/* Click to View More */}
          <Button variant="ghost" className="p-0 h-auto text-primary group-hover:text-primary-foreground transition-colors duration-200">
            <span className="text-sm font-medium">Click to view details</span>
            <svg 
              className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform duration-200" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Button>
        </CardFooter>
      </Card>

      {/* Modal */}
      <ProjectModal 
        project={project} 
        isOpen={isModalOpen} 
        onClose={closeModal} 
      />
    </>
  );
}
